/**
 * A Coast Guard form rendered as the document it prints, read-only.
 *
 * Several forms are authored somewhere else entirely — the roster, check-in,
 * resource requests, the calendar — and have never had a planning card, so a
 * user could only find out what the sheet would say by exporting it. This
 * renders the same numbered boxes from the same values the exporter fills in,
 * and points at where each is edited.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { FormExportButton } from 'components/IAP/FormExportButton';
import { useIcsFormPdfPreview } from 'components/IAP/context/IcsFormPdfPreviewContext';
import { FormVersioningBanner } from 'components/IAP/femaCards/FormVersioningBanner';
import { useIcsFormInputs } from 'components/IAP/hooks/icsFormInputsHooks';
import { NULL_VALUE } from 'constants/displayConstants';

import { Ics204CardSection } from '../AssignmentList/ics204BoxLabels';
import { PrintedValue } from '../icsBoxPrimitives';
import { OperationalPeriodScheduleTrigger } from '../OperationalPeriodScheduleControl';

export type DocumentColumn = {
  /** Key on the row object, or the name of the parallel array. */
  readonly field: string;
  readonly label: string;
};

export type IcsDocumentBox =
  /** One value, printed as the sheet prints it. */
  | { readonly kind: 'value'; readonly label: string; readonly field: string }
  /** A list of names in one box, as the 207 prints each seat. */
  | { readonly kind: 'names'; readonly label: string; readonly field: string }
  /** Several labelled values sharing one numbered box. */
  | {
      readonly kind: 'fields';
      readonly label: string;
      readonly fields: readonly DocumentColumn[];
    }
  /** A table whose source is an array of row objects. */
  | {
      readonly kind: 'rows';
      readonly label: string;
      readonly field: string;
      readonly columns: readonly DocumentColumn[];
    }
  /**
   * A table whose source is one array per column, indexed in step — how the
   * 211 and 213RR fetchers hand their line items over.
   */
  | {
      readonly kind: 'parallel';
      readonly label: string;
      readonly columns: readonly DocumentColumn[];
    }
  /**
   * A box the card fills itself, named rather than described.
   *
   * For content the fetched values cannot express: the 207's box 4 is an
   * organisation chart, and the flat list of seat names the exporter falls
   * back to is not what the sheet prints.
   */
  | { readonly kind: 'slot'; readonly label: string; readonly slot: string };

export type IcsDocumentCardProps = {
  title: string;
  /** Suffixed Coast Guard name, e.g. `ICS_211_CG`. */
  formname: string;
  exportLabel: string;
  boxes: readonly IcsDocumentBox[];
  /** Extra payload the form's fetcher needs, e.g. `{ requestId }`. */
  inputData?: Record<string, unknown>;
  /**
   * Flattens whatever the fetcher shapes for the PDF into the fields the
   * boxes name — the 221's check-off object becoming rows, for instance.
   */
  derive?: (inputs: Record<string, unknown>) => Record<string, unknown>;
  periodNumber?: number;
  /** Where a user goes to change what this document says. */
  editedIn: { readonly label: string; readonly to?: string };
  /** What fills each `slot` box, keyed by its slot name. */
  slots?: Record<string, ReactNode>;
  /** An entity picker, for forms that print one resource or request. */
  toolbar?: ReactNode;
  /** Skip the fetch until a picker has something selected. */
  enabled?: boolean;
  /**
   * The family-agnostic form id whose version history this card shows, e.g.
   * `ICS_211`. Left off for sheets that are not versioned.
   */
  versionBaseId?: string;
  /** The 221's chain is one resource; every other versioned card leaves this off. */
  formInstanceId?: string;
  testIdPrefix: string;
};

function printable(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.filter(Boolean).map(printable).join(', ');
  if (value instanceof Date) return value.toLocaleString();
  if (typeof value === 'object') return '';
  return String(value);
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

/** How many rows a parallel-array box has: its longest column. */
function parallelRowCount(
  inputs: Record<string, unknown>,
  columns: readonly DocumentColumn[]
): number {
  return columns.reduce(
    (longest, column) => Math.max(longest, asArray(inputs[column.field]).length),
    0
  );
}

function DocumentTable({
  columns,
  rows,
  testId,
}: {
  columns: readonly DocumentColumn[];
  rows: readonly string[][];
  testId: string;
}) {
  return (
    <div className="tw:overflow-x-auto" data-testid={testId}>
      <table className="tw:w-full tw:text-sm">
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.field}
                className="tw:px-2 tw:py-1 tw:text-left tw:font-normal tw:text-muted-foreground tw:whitespace-nowrap"
              >
                {column.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className="tw:px-2 tw:py-2 tw:text-muted-foreground"
                data-testid={`${testId}-empty`}
              >
                No resources documented yet.
              </td>
            </tr>
          ) : (
            rows.map((cells, index) => (
              <tr key={index}>
                {cells.map((cell, cellIndex) => (
                  <td
                    key={columns[cellIndex]?.field ?? cellIndex}
                    className="tw:px-2 tw:py-1 tw:align-top tw:whitespace-pre-line"
                  >
                    {cell.trim() ? cell : NULL_VALUE}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function isOperationalPeriodBox(label: string): boolean {
  return /operational period/i.test(label);
}

function DocumentBox({
  box,
  inputs,
  slots,
  testIdPrefix,
}: {
  box: IcsDocumentBox;
  inputs: Record<string, unknown>;
  slots: Record<string, ReactNode>;
  testIdPrefix: string;
}) {
  const slug = box.label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  const testId = `${testIdPrefix}-section-${slug}`;

  if (box.kind === 'slot') {
    return (
      <Ics204CardSection boxLabel={box.label} testId={testId} className="tw:mt-0">
        {slots[box.slot]}
      </Ics204CardSection>
    );
  }

  if (box.kind === 'value' || box.kind === 'names') {
    const value = (
      <PrintedValue
        value={printable(inputs[box.field])}
        testId={`${testId}-value`}
      />
    );
    return (
      <Ics204CardSection boxLabel={box.label} testId={testId} className="tw:mt-0">
        {isOperationalPeriodBox(box.label) ? (
          <OperationalPeriodScheduleTrigger testId={`${testId}-schedule`}>
            {value}
          </OperationalPeriodScheduleTrigger>
        ) : (
          value
        )}
      </Ics204CardSection>
    );
  }

  if (box.kind === 'fields') {
    const fields = (
      <dl className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
        {box.fields.map((field) => (
          <div key={field.field} className="tw:min-w-0">
            <dt className="tw:text-xs tw:text-muted-foreground">
              {field.label}
            </dt>
            <dd className="tw:m-0">
              <PrintedValue
                value={printable(inputs[field.field])}
                testId={`${testId}-${field.field}`}
              />
            </dd>
          </div>
        ))}
      </dl>
    );
    return (
      <Ics204CardSection boxLabel={box.label} testId={testId} className="tw:mt-0">
        {isOperationalPeriodBox(box.label) ? (
          <OperationalPeriodScheduleTrigger testId={`${testId}-schedule`}>
            {fields}
          </OperationalPeriodScheduleTrigger>
        ) : (
          fields
        )}
      </Ics204CardSection>
    );
  }

  const rows =
    box.kind === 'rows'
      ? asArray(inputs[box.field]).map((row) =>
          box.columns.map((column) =>
            printable((row as Record<string, unknown>)?.[column.field])
          )
        )
      : Array.from({ length: parallelRowCount(inputs, box.columns) }, (_, index) =>
          box.columns.map((column) =>
            printable(asArray(inputs[column.field])[index])
          )
        );

  return (
    <Ics204CardSection boxLabel={box.label} testId={testId} className="tw:mt-0">
      <DocumentTable columns={box.columns} rows={rows} testId={`${testId}-table`} />
    </Ics204CardSection>
  );
}

export function IcsDocumentCard({
  title,
  formname,
  exportLabel,
  boxes,
  inputData,
  derive,
  periodNumber,
  editedIn,
  slots = {},
  toolbar,
  enabled,
  versionBaseId,
  formInstanceId,
  testIdPrefix,
}: IcsDocumentCardProps) {
  const { exportAsForm, isExporting } = useIcsFormPdfPreview();
  const {
    data: inputs,
    isPending,
    isError,
  } = useIcsFormInputs(formname, { data: inputData, periodNumber, enabled });
  const printed = inputs ? (derive ? derive(inputs) : inputs) : {};

  return (
    <Card className="dark tw:bg-background tw:text-foreground">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardAction>
          <FormExportButton
            formname={formname}
            label={exportLabel}
            disabled={isExporting}
            onExport={(format) =>
              void exportAsForm({
                formname,
                data: inputData ?? {},
                mapSketch: null,
                format,
                periodNumber,
              })
            }
          />
        </CardAction>
      </CardHeader>
      <CardContent className="tw:flex tw:min-w-0 tw:flex-col tw:gap-3">
        <p
          className="tw:m-0 tw:text-xs tw:text-muted-foreground"
          data-testid={`${testIdPrefix}-edited-in`}
        >
          This sheet is filled in from{' '}
          {editedIn.to ? (
            <Link to={editedIn.to} className="tw:underline">
              {editedIn.label}
            </Link>
          ) : (
            editedIn.label
          )}
          . Edit it there and it prints here.
        </p>

        {versionBaseId ? (
          <FormVersioningBanner
            baseId={versionBaseId}
            formInstanceId={formInstanceId}
          />
        ) : null}

        {toolbar}

        {enabled === false ? null : isPending ? (
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            Loading the document…
          </p>
        ) : isError ? (
          <p className="tw:m-0 tw:text-sm tw:text-muted-foreground">
            This document could not be read. Try the export to see the error.
          </p>
        ) : (
          boxes.map((box) => (
            <DocumentBox
              key={box.label}
              box={box}
              inputs={printed}
              slots={slots}
              testIdPrefix={testIdPrefix}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}

export default IcsDocumentCard;
